/**
 * Minimal SSE client using fetch + ReadableStream so we can send
 * Authorization Bearer tokens (EventSource doesn't support headers).
 *
 * Usage:
 *   const controller = streamSSE(url, {
 *     headers: { Authorization: `Bearer ${token}` },
 *     onEvent: (name, data) => { ... },
 *     onError: (e) => { ... },
 *     onDone:  () => { ... },
 *   });
 *   // later: controller.abort();
 */
export function streamSSE(url, { headers = {}, onEvent, onError, onDone }) {
  const abort = new AbortController();

  (async () => {
    try {
      const res = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: { Accept: "text/event-stream", ...headers },
        signal: abort.signal,
      });
      if (!res.ok || !res.body) {
        onError?.(new Error(`SSE HTTP ${res.status}`));
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Split on blank lines (SSE record separator).
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const rawEvent = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          let eventName = "message";
          const dataLines = [];
          for (const line of rawEvent.split("\n")) {
            if (line.startsWith("event:")) eventName = line.slice(6).trim();
            else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
          }
          if (!dataLines.length) continue;
          const dataStr = dataLines.join("\n");
          let data;
          try { data = JSON.parse(dataStr); } catch { data = dataStr; }
          onEvent?.(eventName, data);
        }
      }
      onDone?.();
    } catch (e) {
      if (e.name === "AbortError") return;
      onError?.(e);
    }
  })();

  return { abort: () => abort.abort() };
}
